import {
  SearchableSelect,
  type SearchableSelectItem,
} from "@/components/molecules/searchable-select";
import { useSchedules } from "@/features/calendar/hooks/use-schedules";
import { CalendarBlankIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useTranslation } from "@/features/utils/i18n";

interface ScheduleSelectProps {
  value: string;
  onChange: (value: string) => void;
  allowNone?: boolean;
  placeholder?: string;
}

export function ScheduleSelect({
  value,
  onChange,
  allowNone = true,
  placeholder,
}: ScheduleSelectProps) {
  const { t } = useTranslation();
  const { data: schedules = [], isLoading } = useSchedules();

  // Convert to SearchableSelectItem format
  const items: SearchableSelectItem[] = useMemo(
    () =>
      schedules.map((schedule) => ({
        id: schedule.id,
        label: schedule.name,
        description: schedule.timezone,
        icon: <CalendarBlankIcon className="h-4 w-4 text-primary" />,
        data: schedule,
      })),
    [schedules],
  );

  return (
    <SearchableSelect
      items={items}
      value={value}
      onChange={(v) => onChange(Array.isArray(v) ? v[0] || "" : v)}
      multiple={false}
      placeholder={placeholder ?? t("common.select.schedule.placeholder")}
      searchPlaceholder={t("common.select.schedule.search")}
      emptyText={t("common.select.schedule.empty")}
      isLoading={isLoading}
      allowNone={allowNone}
      noneOption={{
        id: "NONE",
        label: t("common.select.schedule.none"),
        description: t("common.select.schedule.noneDescription"),
        icon: <CalendarBlankIcon className="h-4 w-4" weight="fill" />,
      }}
    />
  );
}
